import { OverlapTransitionStateContext } from "@redotech/react-animation/outlet-transition";
import { OverlapTransitionState } from "@redotech/react-animation/transition";
import { RouterOutletFade } from "@redotech/react-router-util/outlet-fade";
import { useHtmlEventListener } from "@redotech/react-util/event";
import classnames from "classnames";
import {
  ComponentType,
  ReactNode,
  RefObject,
  Suspense,
  createContext,
  lazy,
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Location, useLocation } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useTabState } from "./arbiter-components/tabs/use-tab-state";
import { Breadcrumb, Breadcrumbs } from "./breadcrumb";
import { Flex } from "./flex";
import { PageHeaderSlotContext } from "./page-header-slot";
import type {
  PageAction,
  PageLeadingVisual,
  PageSubtitle,
  PageTab,
} from "./page-prop-types";
import * as pageCss from "./page.module.css";

export type {
  PageAction,
  PageLeadingVisual,
  PageSubtitle,
  PageTab,
} from "./page-prop-types";

// Lazy so MUI-backed components stay out of `page.tsx`'s static import graph —
// downstream Jest suites transitively import this file and can't parse MUI ESM.
const ArbiterPageHeader = lazy(() => import("./arbiter-page-header"));

export const Action0PortalContext = createContext<HTMLElement | undefined>(
  undefined,
);

export const ActionPortalContext = createContext<HTMLElement | undefined>(
  undefined,
);

export const TabsPortalContext = createContext<{
  portalNode: HTMLElement | undefined;
}>({ portalNode: undefined });

export const NavOverlayExpandedContext = createContext<{
  setNavOverlayExpanded: (expanded: boolean) => void;
}>({ setNavOverlayExpanded: () => {} });

export const NavShowLeftPaddingContext = createContext<{
  setShowLeftPadding: (show: boolean) => void;
  setHasSecondaryNav: (has: boolean) => void;
}>({ setShowLeftPadding: () => {}, setHasSecondaryNav: () => {} });

/**
 * A gotcha: The rendered ReactNode wont be able to access any of its parents contexts,
 * so it must be defined with that in mind.
 */
export const HeaderOverrideContext = createContext<
  (node: ReactNode | null) => void
>(() => {});

/**
 * Used to correctly calculate infinite scroll boundaries
 */
export const PageMainContentRefContext = createContext<
  RefObject<HTMLDivElement> | undefined
>(undefined);

export const Actions = memo(function Actions({
  show,
  children,
  noBorder = false,
}: {
  children: ReactNode | ReactNode[];
  show: boolean;
  noBorder?: boolean;
}) {
  const transitionState = useContext(OverlapTransitionStateContext);

  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [goal, setGoal] = useState(show);
  const [finished, setFinished] = useState(true);

  useHtmlEventListener(ref, "transitionend", (e) => {
    if (e.propertyName !== "opacity") {
      return;
    }
    setFinished(true);
  });

  useLayoutEffect(() => {
    if (!ref || show === goal) {
      return;
    }
    void ref.offsetHeight;
    setGoal(show);
    setFinished(false);
  }, [show, ref, goal]);

  if (
    (!show && !goal && finished) ||
    transitionState === OverlapTransitionState.EXIT
  ) {
    return null;
  }

  return (
    <div
      className={classnames(pageCss.actions, !noBorder && pageCss.border, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [pageCss.collapse]: !goal,
      })}
      ref={setRef}
    >
      {children}
    </div>
  );
});

let scrollXCount = 0;

export function useScrollableWidth() {
  useLayoutEffect(() => {
    if (!scrollXCount++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      document.documentElement.classList.add(pageCss.noScrollX);
    }
    return () => {
      if (!--scrollXCount) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        document.documentElement.classList.remove(pageCss.noScrollX);
      }
    };
  }, []);
}

let scrollYCount = 0;

export function useScrollableHeight() {
  useLayoutEffect(() => {
    if (!scrollYCount++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      document.documentElement.classList.add(pageCss.noScrollY);
    }
    return () => {
      if (!--scrollYCount) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        document.documentElement.classList.remove(pageCss.noScrollY);
      }
    };
  }, []);
}

export enum TabContentChangedEventType {
  CHECK_CHILDREN = "CHECK_CHILDREN",
  EXPLICITLY_TOLD_ZERO_CHILDREN = "CHECK_GRANDCHILDREN",
}

export const Page = memo(function Page({
  nav,
  banner,
  profile,
  hidePadding,
  hideOuterTopPadding,
  hideHeader = (url: string) => false,
  ErrorBoundary,
  showNewIntercomMessage,
  skipFirstBreadcrumbIfMatches,
  newChrome = false,
  title,
  subtitle,
  breadcrumbs,
  leadingVisual,
  actions,
  tabs,
  outerClassName,
}: {
  nav: ReactNode;
  banner?: ReactNode;
  profile?: ReactNode;
  hidePadding?: boolean;
  /**
   * Drop the dark gutter above the page body. Set when a sibling above
   * (e.g. a top nav bar) already provides the same dark band, so the
   * extra padding-top doesn't read as asymmetric space below the nav.
   */
  hideOuterTopPadding?: boolean;
  hideHeader?: (url: string, search: { search: string }) => boolean;
  ErrorBoundary?: ComponentType<{
    children: ReactNode;
    location: Location;
    showNewIntercomMessage?: () => void;
  }>;
  showNewIntercomMessage?: () => void;
  /**
   * List of breadcrumb names to filter out if they appear as the first breadcrumb.
   * Useful for hiding top-level navigation categories (e.g., "Home", "Order Management")
   * from the breadcrumb trail.
   */
  skipFirstBreadcrumbIfMatches?: string[];
  /**
   * Opt every route under this mount into the new `RedoPageHeader` chrome.
   * Un-migrated areas leave this false and keep the legacy chrome.
   */
  newChrome?: boolean;
  // Direct prop overrides for the new header. Leaves usually register values
  // via the `usePage*` hooks instead; hook-registered values win over these.
  title?: string;
  subtitle?: PageSubtitle;
  breadcrumbs?: Breadcrumb[];
  leadingVisual?: PageLeadingVisual;
  actions?: PageAction[];
  tabs?: PageTab[];
  outerClassName?: string;
}) {
  const [navCollapsed] = useLocalStorage(
    "redo_side_navigation_collapse_mode",
    false,
  );
  const [showLeftPadding, setShowLeftPadding] = useState(false);
  const [hasSecondaryNav, setHasSecondaryNav] = useState(false);

  const [actions0Element, setActions0Element] = useState<HTMLElement | null>(
    null,
  );
  const [actionsElement, setActionsElement] = useState<HTMLElement | null>(
    null,
  );
  const [headerOverrideElement, setHeaderOverrideElement] =
    useState<ReactNode | null>(null);
  const [tabsElement, setTabsElement] = useState<HTMLElement | null>(null);

  const [slotTitle, setSlotTitle] = useState<string | undefined>(undefined);
  const [slotSubtitle, setSlotSubtitle] = useState<PageSubtitle | undefined>(
    undefined,
  );
  const [slotBreadcrumbs, setSlotBreadcrumbs] = useState<
    Breadcrumb[] | undefined
  >(undefined);
  const [slotLeadingVisual, setSlotLeadingVisual] = useState<
    PageLeadingVisual | undefined
  >(undefined);
  const [slotActions, setSlotActions] = useState<PageAction[] | undefined>(
    undefined,
  );
  const [slotTabs, setSlotTabs] = useState<PageTab[] | undefined>(undefined);
  const [slotHideTitle, setSlotHideTitle] = useState<boolean>(false);

  const slotSetters = useMemo(
    () => ({
      setTitle: setSlotTitle,
      setSubtitle: setSlotSubtitle,
      setBreadcrumbs: setSlotBreadcrumbs,
      setLeadingVisual: setSlotLeadingVisual,
      setActions: setSlotActions,
      setTabs: setSlotTabs,
      setHideTitle: setSlotHideTitle,
    }),
    [],
  );

  const mergedTitle = slotTitle ?? title;
  const mergedSubtitle = slotSubtitle ?? subtitle;
  const mergedBreadcrumbs = slotBreadcrumbs ?? breadcrumbs;
  const mergedLeadingVisual = slotLeadingVisual ?? leadingVisual;
  const mergedActions = slotActions ?? actions;
  const mergedTabs = slotTabs ?? tabs;
  const mergedHideTitle = slotHideTitle;

  const location = useLocation();

  const [hideHeader_, setHideHeader_] = useState(false);

  useEffect(() => {
    setHideHeader_(hideHeader(location.pathname, { search: location.search }));
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const mainContentRef = useRef<HTMLDivElement>(null);

  const tabState = useTabState(mergedTabs);

  // Only direct-prop tabs override the body. Slot-pushed tabs would create an
  // infinite loop: overriding the body unmounts the outlet → unmounts the leaf
  // → clears the slot → clears the override → remounts the outlet…
  const bodyContent: ReactNode | undefined =
    tabs !== undefined ? tabState.activeContent : undefined;

  const pageHeader = (
    <PageHeader
      actions={mergedActions}
      breadcrumbs={mergedBreadcrumbs}
      headerOverrideElement={headerOverrideElement}
      hideHeader={hideHeader_}
      hideTitle={mergedHideTitle}
      leadingVisual={mergedLeadingVisual}
      newChrome={newChrome}
      profile={profile}
      setActions0Element={setActions0Element}
      setActionsElement={setActionsElement}
      setTabsElement={setTabsElement}
      skipFirstBreadcrumbIfMatches={skipFirstBreadcrumbIfMatches}
      subtitle={mergedSubtitle}
      tabs={mergedTabs}
      tabSelectedKey={tabState.selectedKey}
      tabSetSelectedKey={tabState.setSelectedKey}
      title={mergedTitle}
    />
  );

  const pageContent = (
    <PageContent
      content={bodyContent}
      ErrorBoundary={ErrorBoundary}
      hidePadding={hidePadding}
      mainContentRef={mainContentRef}
      showNewIntercomMessage={showNewIntercomMessage}
    />
  );

  const pageContents = (
    <Flex
      dir="column"
      gap="none"
      grow={1}
      overflow="hidden"
      style={{ maxWidth: "100%" }}
    >
      <Flex
        className={classnames(pageCss.pageOuter, outerClassName, {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [pageCss.showLeftPadding]: showLeftPadding,
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [pageCss.noTopPadding]: hideOuterTopPadding,
        })}
        gap="none"
        grow={1}
        overflow="hidden"
      >
        <PageNavigation navigation={nav} />
        <Flex
          as="main"
          className={classnames(pageCss.pageMain, {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [pageCss.hasLeftRadius]: !hasSecondaryNav,
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [pageCss.navCollapsedGap]: navCollapsed && !showLeftPadding,
          })}
          dir="column"
          gap="none"
          grow={1}
          overflow="hidden"
        >
          {banner}
          {pageHeader}
          {pageContent}
        </Flex>
      </Flex>
    </Flex>
  );

  return (
    <NavShowLeftPaddingContext.Provider
      value={{ setShowLeftPadding, setHasSecondaryNav }}
    >
      <PageMainContentRefContext.Provider value={mainContentRef ?? undefined}>
        <HeaderOverrideContext.Provider value={setHeaderOverrideElement}>
          <Action0PortalContext.Provider value={actions0Element || undefined}>
            <ActionPortalContext.Provider value={actionsElement || undefined}>
              <TabsPortalContext.Provider
                value={{ portalNode: tabsElement || undefined }}
              >
                <PageHeaderSlotContext.Provider value={slotSetters}>
                  {pageContents}
                </PageHeaderSlotContext.Provider>
              </TabsPortalContext.Provider>
            </ActionPortalContext.Provider>
          </Action0PortalContext.Provider>
        </HeaderOverrideContext.Provider>
      </PageMainContentRefContext.Provider>
    </NavShowLeftPaddingContext.Provider>
  );
});

const PageContent = memo(function PageContent({
  ErrorBoundary,
  showNewIntercomMessage,
  mainContentRef,
  hidePadding = false,
  content,
}: {
  ErrorBoundary?: ComponentType<{
    children: ReactNode;
    location: Location;
    showNewIntercomMessage?: () => void;
  }>;
  showNewIntercomMessage?: () => void;
  mainContentRef: RefObject<HTMLDivElement>;
  hidePadding: boolean | undefined;
  content?: ReactNode;
}) {
  const location = useLocation();

  const body =
    content !== undefined ? (
      <div
        className={classnames(!hidePadding && pageCss.pagePadding)}
        ref={mainContentRef}
      >
        {content}
      </div>
    ) : (
      <RouterOutletFade
        childClassName={classnames(!hidePadding && pageCss.pagePadding)}
        element="div"
        elementRef={mainContentRef}
      />
    );

  if (ErrorBoundary) {
    return (
      <ErrorBoundary
        location={location}
        showNewIntercomMessage={showNewIntercomMessage}
      >
        {body}
      </ErrorBoundary>
    );
  }
  return body;
});

const PageNavigation = memo(function PageNavigation({
  navigation,
}: {
  navigation: ReactNode;
}) {
  return <Flex as="nav">{navigation}</Flex>;
});

const PageHeader = memo(function PageHeader({
  headerOverrideElement,
  hideHeader,
  hideTitle,
  setActions0Element,
  setActionsElement,
  setTabsElement,
  profile,
  skipFirstBreadcrumbIfMatches,
  newChrome,
  title,
  subtitle,
  breadcrumbs,
  leadingVisual,
  actions,
  tabs,
  tabSelectedKey,
  tabSetSelectedKey,
}: {
  hideHeader: boolean;
  hideTitle: boolean;
  headerOverrideElement: ReactNode | null;
  setActions0Element: (node: HTMLElement | null) => void;
  setActionsElement: (node: HTMLElement | null) => void;
  setTabsElement: (node: HTMLElement | null) => void;
  profile: ReactNode | undefined;
  skipFirstBreadcrumbIfMatches?: string[];
  newChrome: boolean;
  title?: string;
  subtitle?: PageSubtitle;
  breadcrumbs?: Breadcrumb[];
  leadingVisual?: PageLeadingVisual;
  actions?: PageAction[];
  tabs?: PageTab[];
  tabSelectedKey: string | undefined;
  tabSetSelectedKey: (key: string) => void;
}) {
  if (hideHeader) {
    return null;
  }

  if (headerOverrideElement) {
    return <HeaderContainer>{headerOverrideElement}</HeaderContainer>;
  }

  // Opt-in: either the route mount sets `newChrome`, or a leaf passes/registers
  // any header slot. Otherwise fall through to the legacy header.
  const usingNewHeader =
    newChrome ||
    hideTitle ||
    title !== undefined ||
    subtitle !== undefined ||
    breadcrumbs !== undefined ||
    leadingVisual !== undefined ||
    actions !== undefined ||
    tabs !== undefined;

  if (!usingNewHeader) {
    return (
      <HeaderContainer>
        <>
          <Flex className={pageCss.headerRow} justify="space-between">
            <Breadcrumbs skipFirstIfMatches={skipFirstBreadcrumbIfMatches} />
            <Flex>
              <div
                className={pageCss.actionsContainer}
                ref={setActions0Element}
              />
              <div
                className={pageCss.actionsContainer}
                ref={setActionsElement}
              />
              {profile}
            </Flex>
          </Flex>
          <div className={pageCss.tabsWrapper} ref={setTabsElement} />
        </>
      </HeaderContainer>
    );
  }

  return (
    <Suspense fallback={<HeaderContainer>{null}</HeaderContainer>}>
      <ArbiterPageHeader
        actions={actions}
        breadcrumbs={breadcrumbs}
        hideTitle={hideTitle}
        leadingVisual={leadingVisual}
        profile={profile}
        setActions0Element={setActions0Element}
        setActionsElement={setActionsElement}
        setTabsElement={setTabsElement}
        subtitle={subtitle}
        tabs={tabs}
        tabSelectedKey={tabSelectedKey}
        tabSetSelectedKey={tabSetSelectedKey}
        title={title}
      />
    </Suspense>
  );
});

const HeaderContainer = memo(function HeaderContainer({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Flex
      as="header"
      bgColor="primary"
      borderBottomWidth="1px"
      borderColor="primary"
      borderStyle="solid"
      className={classnames(pageCss.headerContainer)}
      dir="column"
      gap="none"
      px="3xl"
    >
      {children}
    </Flex>
  );
});
